import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "redux/store";
import { getLastRoomsAvailable } from "redux/reducers/Properties/PropertiesSlice";
import { setSearchData } from "redux/reducers/Search/SearchSlice";
import { Flame, ArrowRight, Bed, ChevronDown } from "lucide-react";
import {
  LastRoomsProperty,
  LastAvailableRoom,
} from "redux/reducers/Properties/PropertiesSliceTypes";

interface Props {
  startDate: string;
  endDate: string;
  maxProperties?: number;        // default 2 for homepage, undefined = show all
  occupancyThreshold?: number;   // default 50
  showEmptyState?: boolean;      // false on homepage (silent), true on dedicated page
}

// How many rooms to show per property before "Show more"
const INITIAL_ROOMS = 4;

// Parse "YYYY-MM-DD" as a local date (avoids UTC off-by-one)
const parseYMD = (dateStr: string) => {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const formatMonthYear = (dateStr: string) =>
  parseYMD(dateStr).toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
  });

const formatAvailFrom = (dateStr: string) =>
  parseYMD(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const LastRoomsAvailableSection: React.FC<Props> = ({
  startDate,
  endDate,
  maxProperties,
  occupancyThreshold = 50,
  showEmptyState = false,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const { lastRoomsAvailable, loading } = useSelector(
    (state: RootState) => state.property
  );
  const storedSearchData = useSelector(
    (state: RootState) => state.search.searchedData
  );

  useEffect(() => {
    dispatch(getLastRoomsAvailable({ startDate, endDate, occupancyThreshold }));
  }, [startDate, endDate, occupancyThreshold, dispatch]);

  const handleBookNow = (slug: string) => {
    dispatch(
      setSearchData({
        ...storedSearchData,
        startDate: startDate,
        endDate: endDate,
      })
    );
    navigate(`/listing-stay-detail/${slug}?navigatedFrom=last_rooms`);
  };

  const handleSeeAll = () => {
    navigate(`/last-rooms?start_date=${startDate}&end_date=${endDate}`);
  };

  const toggleExpanded = (id: string) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-orange-100 border-t-orange-500 rounded-full animate-spin" />
      </div>
    );
  }

  // Empty state
  if (!lastRoomsAvailable || lastRoomsAvailable.properties.length === 0) {
    if (!showEmptyState) return null;
    return (
      <div className="py-12 text-center px-4">
        <div className="w-16 h-16 mx-auto mb-4 bg-orange-50 dark:bg-orange-900/20 rounded-full flex items-center justify-center">
          <Bed size={28} className="text-orange-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          No rooms filling up right now
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Check back soon — properties fill up as the semester approaches.
        </p>
      </div>
    );
  }

  const { properties, meta } = lastRoomsAvailable;
  const displayProps = maxProperties
    ? properties.slice(0, maxProperties)
    : properties;
  const hasMore = !!maxProperties && properties.length > maxProperties;
  const startLabel = formatMonthYear(startDate);
  const endLabel = formatMonthYear(endDate);
  const searchStart = parseYMD(startDate);
  const singleProperty = displayProps.length === 1;

  // Rooms go 2-up when the card is wide enough:
  // - one property: card is full width, so 2 columns from sm up
  // - two+ properties: cards sit side by side at lg, so wait until xl
  const roomGridCols = singleProperty
    ? "sm:grid-cols-2"
    : "sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2";

  return (
    <section className="py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-6 gap-4">
        <div>
          <div className="inline-flex items-center gap-1.5 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-xs font-medium px-3 py-1 rounded-full mb-3">
            <Flame size={12} />
            Almost full
          </div>
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mb-1">
            Last rooms available
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {startLabel} — {endLabel} semester
            <span className="hidden sm:inline"> &nbsp;·&nbsp; </span>
            <br className="sm:hidden" />
            {meta.total_count}{" "}
            {meta.total_count === 1 ? "property" : "properties"} filling up fast
          </p>
        </div>

        {hasMore && (
          <button
            onClick={handleSeeAll}
            className="self-start sm:self-auto inline-flex items-center gap-1.5 text-sm font-medium text-orange-600 hover:text-orange-700 dark:text-orange-400"
          >
            See all
            <ArrowRight size={16} />
          </button>
        )}
      </div>

      {/* Property cards */}
      <div
        className={`grid grid-cols-1 gap-5 ${
          singleProperty ? "" : "lg:grid-cols-2"
        }`}
      >
        {displayProps.map((prop: LastRoomsProperty) => {
          const id = String(prop.id);
          const occ = Math.round(prop.occupancy_percentage);
          const roomsLabel =
            prop.available_rooms_count === 1
              ? "1 room left"
              : `${prop.available_rooms_count} rooms left`;

          const rooms = prop.available_rooms;
          const isExpanded = !!expanded[id];
          const hiddenCount = rooms.length - INITIAL_ROOMS;
          const visibleRooms =
            isExpanded || hiddenCount <= 0
              ? rooms
              : rooms.slice(0, INITIAL_ROOMS);

          return (
            <div
              key={prop.id}
              className="flex flex-col h-full bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
            >
              {/* Cover image */}
              <div className="relative h-40 sm:h-48 overflow-hidden flex-shrink-0">
                <img
                  src={prop.cover_image?.url}
                  alt={prop.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-red-600/90 text-white text-xs font-medium px-3 py-1 rounded-full">
                  <Flame size={11} />
                  {occ}% full
                </div>
                <div className="absolute top-3 right-3 bg-black/60 text-white text-xs font-medium px-3 py-1 rounded-full">
                  {roomsLabel}
                </div>
              </div>

              {/* Property info */}
              <div className="px-4 pt-3 pb-3">
                <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1 truncate">
                  {prop.neighborhood} · {prop.city}
                </p>
                <p className="text-sm sm:text-base font-medium text-gray-900 dark:text-white leading-snug mb-3">
                  {prop.title}
                </p>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-1.5 bg-gray-100 dark:bg-neutral-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-orange-500 rounded-full transition-all"
                      style={{ width: `${occ}%` }}
                    />
                  </div>
                  <span className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">
                    {occ}% booked
                  </span>
                </div>
              </div>

              <div className="border-t border-gray-100 dark:border-neutral-700 mx-4" />

              {/* Available rooms */}
              <div className="flex-1 flex flex-col p-3 sm:p-4">
                <div className={`grid grid-cols-1 ${roomGridCols} gap-2.5`}>
                  {visibleRooms.map((room: LastAvailableRoom) => {
                    const imgUrl = room.room_images?.[0]?.url;
                    const showAvailFrom =
                      parseYMD(room.availability_start) > searchStart;

                    return (
                      <div
                        key={room.id}
                        className="flex items-center gap-3 bg-gray-50 dark:bg-neutral-700/50 rounded-xl p-2.5 min-w-0"
                      >
                        {/* Room thumbnail */}
                        {imgUrl ? (
                          <img
                            src={imgUrl}
                            alt={room.name}
                            className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-lg bg-gray-200 dark:bg-neutral-600 flex items-center justify-center flex-shrink-0">
                            <Bed size={18} className="text-gray-400 dark:text-gray-500" />
                          </div>
                        )}

                        {/* Room details */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {room.name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {room.size}m² · {room.max_guests}{" "}
                            {room.max_guests === 1 ? "guest" : "guests"}
                          </p>
                          {showAvailFrom && (
                            <p className="text-xs text-orange-600 dark:text-orange-400 truncate">
                              From {formatAvailFrom(room.availability_start)}
                            </p>
                          )}
                        </div>

                        {/* Price + Book */}
                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                          <span className="text-sm font-semibold text-gray-900 dark:text-white whitespace-nowrap">
                            ${room.price_per_month.toLocaleString()}
                            <span className="text-xs font-normal text-gray-400 dark:text-gray-500">
                              /mo
                            </span>
                          </span>
                          <button
                            onClick={() => handleBookNow(prop.slug)}
                            className="bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white text-xs font-medium px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap"
                          >
                            Book now
                          </button>
                        </div>
                      </div>
                    );
                    
                  })}
                </div>

                {/* Show more / less */}
                {hiddenCount > 0 && (
                  <button
                    onClick={() => toggleExpanded(id)}
                    className="mt-auto pt-3 inline-flex items-center justify-center gap-1 text-sm font-medium text-orange-600 hover:text-orange-700 dark:text-orange-400 w-full"
                  >
                    {isExpanded
                      ? "Show less"
                      : `Show ${hiddenCount} more ${hiddenCount === 1 ? "room" : "rooms"}`}
                    <ChevronDown
                      size={16}
                      className={`transition-transform ${isExpanded ? "rotate-180" : ""}`}
                    />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default LastRoomsAvailableSection;